import {
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  doc,
  updateDoc,
  documentId,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Registration } from '../types';
import { getEventEdition, RaceDistance } from './eventEditionService';

// ---------------------------------------------------------------------------
// CSV parsing
// ---------------------------------------------------------------------------

export interface ParsedTimingRow {
  bibNumber: number;
  finalPosition: number;
  timeDisplay: string;
  timeSeconds: number;
}

/** Parse a single CSV line respecting double-quoted fields (RFC 4180).
 * Handles doubled quotes ("") inside quoted fields.
 */
const parseCsvLine = (line: string): string[] => {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    const nextCh = line[i + 1];
    if (ch === '"') {
      if (inQuotes && nextCh === '"') {
        // Doubled quote inside quoted field → single quote
        current += '"';
        i++; // skip second quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      fields.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current.trim());
  return fields;
};

/**
 * Parse time string "HH:MM:SS.t" → seconds (with tenths).
 * Age-grading uses tenths precision, so we keep one decimal.
 */
export const parseRaceSplitterTime = (timeStr: string): number | null => {
  const s = timeStr.trim();
  if (!s || s === '-') return null;
  const parts = s.split(':');
  if (parts.length !== 3) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const sec = parseFloat(parts[2].replace(',', '.'));
  if (isNaN(h) || isNaN(m) || isNaN(sec)) return null;
  return Math.round((h * 3600 + m * 60 + sec) * 10) / 10;
};

/**
 * Parse a RaceSplitter CSV export.
 * Expected header: Final Position,"Bib Number","Last Name","First Name","Team","Total Elapsed Time",...
 *
 * Handles Excel-style CSV where the entire line may be wrapped in quotes
 * and internal quotes are doubled (RFC 4180).
 */
export const parseMoRaceSplitterCsv = (csvText: string): ParsedTimingRow[] => {
  const lines = csvText.trim().split(/\r?\n/);
  if (lines.length < 2) return [];

  // Helper: strip outer quotes if entire line is quoted
  const stripOuterQuotes = (line: string): string => {
    if (line.length >= 2 && line.startsWith('"') && line.endsWith('"')) {
      return line.slice(1, -1);
    }
    return line;
  };

  const headers = parseCsvLine(stripOuterQuotes(lines[0])).map((h) => h.toLowerCase());
  const bibIdx = headers.findIndex((h) => h.includes('bib'));
  const posIdx = headers.findIndex((h) => h.includes('position'));
  const timeIdx = headers.findIndex((h) => h.includes('total elapsed'));

  if (bibIdx === -1 || timeIdx === -1) {
    throw new Error(
      'CSV is missing required columns "Bib Number" and/or "Total Elapsed Time"'
    );
  }

  const rows: ParsedTimingRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = stripOuterQuotes(lines[i].trim());
    if (!line) continue;
    const cols = parseCsvLine(line);
    const bibNum = parseInt(cols[bibIdx] ?? '', 10);
    if (isNaN(bibNum) || bibNum <= 0) continue;
    const timeSeconds = parseRaceSplitterTime(cols[timeIdx] ?? '');
    if (timeSeconds === null || timeSeconds <= 0) continue;
    rows.push({
      bibNumber: bibNum,
      finalPosition: posIdx >= 0 ? (parseInt(cols[posIdx] ?? '', 10) || i) : i,
      timeDisplay: (cols[timeIdx] ?? '').trim(),
      timeSeconds,
    });
  }
  return rows;
};

// ---------------------------------------------------------------------------
// Class mapping
// ---------------------------------------------------------------------------

export type MoResultClass = 'competition' | 'trim' | 'tur';

const raceDistanceToClass = (
  raceDistanceId: string,
  raceDistances: RaceDistance[]
): MoResultClass => {
  const id = raceDistanceId.toLowerCase().trim();
  const rd = raceDistances.find((r) => r.id === raceDistanceId);
  const checkId = rd?.id?.toLowerCase() ?? id;

  // Explicit checks for known class identifiers
  if (checkId === 'competition' || checkId === 'konkurranse' || checkId.includes('competition')) {
    return 'competition';
  }
  if (checkId === 'trim' || checkId === 'timed_recreation' || checkId.includes('trim')) {
    return 'trim';
  }
  // Turklasse/hiking - check explicit values and partial matches
  if (checkId === 'hiking' || checkId === 'tur' || checkId === 'mosjon' ||
      checkId.includes('tur') || checkId.includes('mosjon') || checkId.includes('hike')) {
    return 'tur';
  }

  // Fallback to displayName_no
  const dn = (rd?.displayName_no ?? rd?.displayName ?? '').toLowerCase();
  if (dn.includes('konkurranse') || dn.includes('competition')) return 'competition';
  if (dn.includes('trim')) return 'trim';
  if (dn.includes('tur') || dn.includes('mosjon') || dn.includes('hike')) return 'tur';

  return 'competition';
};

// ---------------------------------------------------------------------------
// Import preview types
// ---------------------------------------------------------------------------

export interface ImportMatch {
  bibNumber: number;
  timeDisplay: string;
  timeSeconds: number;
  registrationId: string;
  firstName: string;
  lastName: string;
  gender: string | null;
  age: number | null;
  representing: string[];
  resultClass: MoResultClass;
  raceDistance: string;
}

export interface UnmatchedBib {
  bibNumber: number;
  timeDisplay: string;
  timeSeconds: number;
}

export interface MissingFromCsv {
  registrationId: string;
  bibNumber: number | null;
  firstName: string;
  lastName: string;
  gender: string | null;
  age: number | null;
  resultClass: MoResultClass;
  /** null = not yet decided by admin */
  status: 'DNS' | 'DNF' | null;
}

export interface TurklasseEntry {
  registrationId: string;
  bibNumber: number | null;
  firstName: string;
  lastName: string;
  gender: string | null;
  /** default FIN; admin can change to DNS or DNF */
  status: 'FIN' | 'DNS' | 'DNF';
}

export interface ImportPreview {
  editionId: string;
  editionYear: number;
  matched: ImportMatch[];
  unmatchedBibs: UnmatchedBib[];
  missingFromCsv: MissingFromCsv[];
  turklasse: TurklasseEntry[];
}

// ---------------------------------------------------------------------------
// Build preview
// ---------------------------------------------------------------------------

const getAge = (dateOfBirth: Date | null | undefined, editionYear: number): number | null => {
  if (!dateOfBirth) return null;
  let dob: Date;
  if (dateOfBirth instanceof Date) {
    dob = dateOfBirth;
  } else if (typeof (dateOfBirth as any).toDate === 'function') {
    // Firestore Timestamp
    dob = (dateOfBirth as any).toDate();
  } else {
    dob = new Date(dateOfBirth as any);
  }
  const birthYear = dob.getFullYear();
  if (isNaN(birthYear) || birthYear < 1900 || birthYear > 2100) return null;
  return editionYear - birthYear;
};

const normalizeGenderFromProfile = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const lower = raw.toLowerCase().trim();
  if (lower === 'male' || lower === 'm') return 'Male';
  if (lower === 'female' || lower === 'f') return 'Female';
  return null;
};

export const buildMoImportPreview = async (
  editionId: string,
  parsedRows: ParsedTimingRow[]
): Promise<ImportPreview> => {
  const edition = await getEventEdition(editionId);
  const editionYear = edition.edition;
  const raceDistances: RaceDistance[] = edition.raceDistances ?? [];

  // Fetch all active registrations for this edition
  const regSnap = await getDocs(
    query(
      collection(db, 'registrations'),
      where('editionId', '==', editionId),
      where('status', 'in', ['pending', 'confirmed'])
    )
  );
  const registrations = regSnap.docs.map(
    (d) => ({ ...(d.data() as Registration), id: d.id } as Registration & { id: string })
  );

  // Batch-fetch user profiles to get gender
  const userIds = Array.from(new Set(registrations.map((r) => r.userId).filter(Boolean) as string[]));
  const genderMap = new Map<string, string | null>();
  if (userIds.length > 0) {
    // Fetch in batches of 30 (Firestore 'in' limit)
    for (let i = 0; i < userIds.length; i += 30) {
      const chunk = userIds.slice(i, i + 30);
      const userSnap = await getDocs(
        query(collection(db, 'users'), where(documentId(), 'in', chunk))
      );
      userSnap.forEach((d) => {
        const g = d.data().gender as string | undefined;
        genderMap.set(d.id, normalizeGenderFromProfile(g ?? null));
      });
    }
  }

  // Build bib → registration map
  const bibToReg = new Map<number, Registration & { id: string }>();
  for (const reg of registrations) {
    if (reg.bibNumber != null) {
      bibToReg.set(reg.bibNumber, reg);
    }
  }

  const matchedBibs = new Set<number>();
  const matched: ImportMatch[] = [];
  const unmatchedBibs: UnmatchedBib[] = [];

  for (const row of parsedRows) {
    const reg = bibToReg.get(row.bibNumber);
    if (!reg) {
      unmatchedBibs.push({
        bibNumber: row.bibNumber,
        timeDisplay: row.timeDisplay,
        timeSeconds: row.timeSeconds,
      });
      continue;
    }
    matchedBibs.add(row.bibNumber);
    const resultClass = raceDistanceToClass(reg.raceDistance, raceDistances);
    const age = getAge(reg.dateOfBirth, editionYear);
    const gender = genderMap.get(reg.userId ?? '') ?? null;
    const representing: string[] = reg.representing ? [reg.representing] : [];

    matched.push({
      bibNumber: row.bibNumber,
      timeDisplay: row.timeDisplay,
      timeSeconds: row.timeSeconds,
      registrationId: reg.id,
      firstName: reg.firstName,
      lastName: reg.lastName,
      gender,
      age,
      representing,
      resultClass,
      raceDistance: reg.raceDistance,
    });
  }

  // Registrations not matched in CSV
  const missingFromCsv: MissingFromCsv[] = [];
  const turklasse: TurklasseEntry[] = [];

  for (const reg of registrations) {
    if (reg.bibNumber != null && matchedBibs.has(reg.bibNumber)) {
      continue;
    }
    const resultClass = raceDistanceToClass(reg.raceDistance, raceDistances);
    const age = getAge(reg.dateOfBirth, editionYear);
    const gender = genderMap.get(reg.userId ?? '') ?? null;

    if (resultClass === 'tur') {
      turklasse.push({
        registrationId: reg.id,
        bibNumber: reg.bibNumber ?? null,
        firstName: reg.firstName,
        lastName: reg.lastName,
        gender,
        status: 'FIN',
      });
    } else {
      missingFromCsv.push({
        registrationId: reg.id,
        bibNumber: reg.bibNumber ?? null,
        firstName: reg.firstName,
        lastName: reg.lastName,
        gender,
        age,
        resultClass,
        status: 'DNS',
      });
    }
  }

  return { editionId, editionYear, matched, unmatchedBibs, missingFromCsv, turklasse };
};

// ---------------------------------------------------------------------------
// Import execution
// ---------------------------------------------------------------------------

export interface ImportEntry {
  registrationId?: string;
  bibNumber?: number | null;
  firstName: string | null;
  lastName: string | null;
  gender: string | null;
  age: number | null;
  representing: string[];
  resultClass: MoResultClass;
  timeDisplay: string | null;
  timeSeconds: number | null;
  /** null means finisher — no status field written */
  status: 'DNS' | 'DNF' | null;
}

export const importMoResults = async (
  editionId: string,
  editionYear: number,
  entries: ImportEntry[]
): Promise<{ written: number; deleted: number }> => {
  const BATCH_SIZE = 400;

  // Delete existing mo-2026 results
  const existingSnap = await getDocs(
    query(collection(db, 'moResults'), where('editionId', '==', editionId))
  );
  const deleted = existingSnap.docs.length;
  for (let i = 0; i < existingSnap.docs.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    existingSnap.docs.slice(i, i + BATCH_SIZE).forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }

  // Write new entries
  const batchId = `mo-racesplitter-${Date.now()}`;
  let written = 0;
  for (let i = 0; i < entries.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = entries.slice(i, i + BATCH_SIZE);
    for (const entry of chunk) {
      const ref = doc(collection(db, 'moResults'));
      const payload: Record<string, unknown> = {
        editionId,
        editionYear,
        firstName: entry.firstName,
        lastName: entry.lastName,
        representing: entry.representing,
        gender: entry.gender ?? null,
        age: entry.age ?? null,
        class: entry.resultClass,
        raceTime: entry.timeDisplay,
        raceTimeSeconds: entry.timeSeconds,
        importBatchId: batchId,
        importedAt: new Date().toISOString(),
        importSource: 'racesplitter',
      };
      if (entry.status) {
        payload.status = entry.status;
      }
      if (entry.bibNumber != null) {
        payload.bibNumber = entry.bibNumber;
      }
      if (entry.registrationId) {
        payload.registrationId = entry.registrationId;
      }
      batch.set(ref, payload);
      written++;
    }
    await batch.commit();
  }

  // Set resultsStatus to 'preliminary'
  await updateDoc(doc(db, 'eventEditions', editionId), {
    resultsStatus: 'preliminary',
  });

  return { written, deleted };
};
